import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormHelperText,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Alert
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';

interface VenueDetailsStepProps {
  data: any;
  onChange: (data: any) => void;
}

const venueTypes = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Café' },
  { value: 'bar', label: 'Bar or Pub' },
  { value: 'event_space', label: 'Event Space' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'resort', label: 'Resort' },
  { value: 'private_venue', label: 'Private Venue' },
  { value: 'other', label: 'Other' }
];

// Days of the week
const daysOfWeek = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const VenueDetailsStep: React.FC<VenueDetailsStepProps> = ({
  data,
  onChange
}) => {
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  
  // Handle basic form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({
      [name]: value
    });
  };
  
  // Handle business hours
  const handleHoursChange = (day: number, time: Date | null, type: 'open' | 'close') => {
    if (!time) return;
    
    const formattedTime = format(time, 'HH:mm');
    const businessHours = { ...(data.businessHours || {}) };
    
    if (!businessHours[day]) {
      businessHours[day] = { open: '09:00', close: '17:00' };
    }
    
    businessHours[day][type] = formattedTime;
    
    onChange({ businessHours });
  };
  
  // Handle image uploads
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const newImages = Array.from(e.target.files);
    setImages([...images, ...newImages]);
    
    // Create preview URLs
    const newPreviewUrls = newImages.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviewUrls]);
    
    onChange({
      venueImages: [...(data.venueImages || []), ...newImages]
    });
  };
  
  // Parse time string to Date object for TimePicker
  const parseTimeString = (timeStr: string) => {
    if (!timeStr) return new Date();
    try {
      return parse(timeStr, 'HH:mm', new Date());
    } catch (error) {
      return new Date();
    }
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Venue Details
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Tell us more about your venue. This information will be displayed to customers when they browse for date experiences.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <TextField
              required
              fullWidth
              label="Venue Name"
              name="venueName"
              value={data.venueName || data.businessName || ''}
              onChange={handleChange}
              placeholder="e.g., The Rooftop Restaurant"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              required
              fullWidth
              select
              label="Venue Type"
              name="venueType"
              value={data.venueType || data.businessType || 'restaurant'}
              onChange={handleChange}
            >
              {venueTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Venue Description"
              name="venueDescription"
              value={data.venueDescription || ''}
              onChange={handleChange}
              placeholder="Describe your venue, atmosphere, and what makes it special for dates..."
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Business Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set your regular business hours. You can customize availability for specific packages later.
            </Typography>
          </Grid>
          
          {daysOfWeek.map((day) => (
            <Grid item xs={12} key={day.value}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body1">{day.label}</Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TimePicker
                      label="Opening Time"
                      value={parseTimeString(data.businessHours?.[day.value]?.open || '09:00')}
                      onChange={(newTime) => handleHoursChange(day.value, newTime, 'open')}
                      ampm={false}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TimePicker
                      label="Closing Time"
                      value={parseTimeString(data.businessHours?.[day.value]?.close || '17:00')}
                      onChange={(newTime) => handleHoursChange(day.value, newTime, 'close')}
                      ampm={false}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Venue Photos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload photos of your venue to showcase it to potential customers. You can add more photos later.
            </Typography>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              sx={{ mb: 2 }}
            >
              Upload Photos
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handleImageUpload}
              />
            </Button>
            
            {previewUrls.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {previewUrls.map((url, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box
                      component="img"
                      src={url}
                      alt={`Venue preview ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            
            {previewUrls.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No photos uploaded yet. Please upload at least one photo of your venue.
              </Alert>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              You can add more detailed information and create packages after completing the setup process.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default VenueDetailsStep;
